<!-- BaseButton.vue - Unified Button Component -->
<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="$emit('click')"
  >
    <!-- Loading spinner -->
    <component
      v-if="loading"
      :is="LoaderIcon"
      class="animate-spin -ml-1 mr-2.5 h-5 w-5 text-current"
    />

    <!-- Left Icon -->
    <component
      v-else-if="icon && iconPosition === 'left'"
      :is="icon"
      :class="[iconSize, iconColorClass]"
      :style="iconOnly ? {} : { marginRight: '0.5rem' }"
    />

    <!-- Text content with Slot support -->
    <span v-if="!iconOnly" class="inline-flex items-center">
      <slot>{{ loading ? loadingText : text }}</slot>
    </span>

    <!-- Right Icon -->
    <component
      v-if="!loading && icon && iconPosition === 'right'"
      :is="icon"
      :class="[iconSize, iconColorClass]"
      :style="iconOnly ? {} : { marginLeft: '0.5rem' }"
    />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'

const LoaderIcon = Loader2

export interface Props {
  text?: string
  loadingText?: string
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'ghost' | 'outline' | 'forest'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  loading?: boolean
  icon?: any
  iconPosition?: 'left' | 'right'
  iconOnly?: boolean
  fullWidth?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  loadingText: 'Loading...',
  type: 'button',
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  icon: undefined,
  iconPosition: 'left',
  iconOnly: false,
  fullWidth: false,
})

defineEmits<{
  click: []
}>()

// Computed classes
const buttonClasses = computed(() => {
  const baseClasses = [
    'inline-flex',
    'justify-center',
    'items-center',
    'font-bold',
    'rounded-[2rem]',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'transform',
    'hover:scale-[1.02]',
    'active:scale-[0.98]',
  ]

  // Size classes
  const sizeClasses = {
    xs: ['px-2.5', 'py-1', 'text-xs'],
    sm: ['px-4', 'py-1.5', 'text-xs md:text-sm'],
    md: ['px-6', 'py-2.5', 'text-sm md:text-base'],
    lg: ['px-8', 'py-3.5', 'text-base md:text-lg'],
    xl: ['px-10', 'py-4', 'text-lg md:text-xl'],
  }

  // Variant classes
  const variantClasses = {
    primary: [
      'text-[#1a1a1a]',
      'bg-primary',
      'hover:bg-primary-hover',
      'active:bg-primary-active',
      'focus:ring-primary/50',
      'shadow-md',
      'hover:shadow-lg',
      'border',
      'border-amber-400/50',
    ],
    secondary: [
      'text-white',
      'bg-secondary',
      'hover:bg-secondary-hover',
      'active:bg-secondary-active',
      'focus:ring-secondary/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
    danger: [
      'text-white',
      'bg-danger',
      'hover:bg-danger-hover',
      'active:bg-danger-active',
      'focus:ring-danger/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
    success: [
      'text-white',
      'bg-success',
      'hover:bg-success-hover',
      'active:bg-success-active',
      'focus:ring-success/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
    warning: [
      'text-white',
      'bg-warning',
      'hover:bg-warning-hover',
      'active:bg-warning-active',
      'focus:ring-warning/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
    ghost: [
      'text-gray-700',
      'dark:text-gray-200',
      'hover:bg-gray-100',
      'dark:hover:bg-gray-700',
      'focus:ring-gray-500',
    ],
    outline: [
      'text-secondary',
      'dark:text-secondary-dark-text',
      'border-2',
      'border-secondary/60',
      'dark:border-secondary-dark/60',
      'hover:bg-secondary-bg',
      'dark:hover:bg-secondary-dark-bg/30',
      'focus:ring-secondary/50',
    ],
    forest: [
      'text-white',
      'bg-[#0b6623]',
      'hover:bg-[#08501b]',
      'active:bg-[#063d14]',
      'focus:ring-[#0b6623]/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
  }

  // Width classes
  const widthClasses = props.fullWidth ? ['w-full'] : []

  // Icon-only classes
  const iconOnlyClasses = props.iconOnly ? ['aspect-square', 'p-2.5'] : []

  return [
    ...baseClasses,
    ...(sizeClasses[props.size] || sizeClasses.md),
    ...(variantClasses[props.variant] || variantClasses.primary),
    ...widthClasses,
    ...iconOnlyClasses,
  ]
})

const iconSize = computed(() => {
  const sizes: Record<string, string> = {
    xs: 'h-3.5 w-3.5',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-5 w-5',
    xl: 'h-6 w-6',
  }
  return sizes[props.size] || 'h-5 w-5'
})

const iconColorClass = computed(() => {
  if (props.variant === 'primary') {
    return 'text-[#1a1a1a]'
  }
  if (props.variant === 'secondary' || props.variant === 'forest') {
    return 'text-white'
  }
  if (props.variant === 'outline') {
    return 'text-secondary dark:text-secondary-dark-text'
  }
  if (props.variant === 'ghost') {
    return 'text-gray-500 dark:text-gray-400'
  }
  return 'text-current'
})
</script>

<style scoped>
button {
  transition: all 0.2s ease-in-out;
}
</style>
